import parse from 'html-react-parser';
import _ from 'lodash';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PageError from 'pages/404';
import { useEffect, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import WidgetBreadcrumb from 'src/2.0/widgets/widget-breadcrumb';
import WidgetHeading from 'src/2.0/widgets/widget-heading';
import CardH from 'src/components/cards/card-h';
import CardV from 'src/components/cards/card-v';
import Layout from 'src/components/layouts';
import { APP_IMG, SHOW_COMMEMORATE } from 'src/constants';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { isShouldFetch } from 'src/utils/detect-scrolling';
import { useDesktop } from 'src/utils/device';
import { convertObjPath } from 'src/utils/helper';
import { GET, GET_ARTICLES } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const DynamicAds = dynamic(import('src/utils/ads/dynamic-ads'));
const PageSearch = ({ articles, lastNews, searchText, nextArticles, ads, dataAdsHeader }) => {
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  if (articles === null) {
    return <PageError dataAdsHeader={dataAdsHeader} sessionSevId={sessionSevId} />;
  }
  const router = useRouter();
  const [adsBox, setAdsBox] = useState(true);
  const handleLightBoxClick = () => {
    setAdsBox(false);
  };

  const nextLinkRef = useRef(nextArticles?.next || null);
  const listArticlesRef = useRef(articles);

  const [listArticles, setListArticles] = useState(articles);
  const [isFetching, setIsFetching] = useState(false);

  const handleScroll = async () => {
    if (isShouldFetch()) {
      document.removeEventListener('scroll', handleScroll);
      await loadMoreArticles();
    }
  };

  const loadMoreArticles = async () => {
    try {
      if (nextArticles.next !== null) {
        setIsFetching(true);

        const resData = await GET_ARTICLES(`${nextLinkRef?.current}`, false, true);

        if (!_.isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.links?.next;
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = _.uniqBy(newLists, 'id');

          setListArticles(listArticlesRef.current);

          if (isShouldFetch()) {
            await loadMoreArticles();
          } else {
            document.addEventListener('scroll', handleScroll);
          }
        }
      }
    } catch (error) {
      console.error('==========> LOAD_MORE ERROR', error.message);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <>
      {/* -------------------------------- NEXT SEO -------------------------------- */}
      <NextSeo
        noindex={true}
        nofollow={true}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
      />
      <Head>
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />
      </Head>
      <Layout sessionSevId={sessionSevId}>
        {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />}
        {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}

        <SectionWrapper id='section-1' className='category-wrapper'>
          <div className='container'>
            <WidgetBreadcrumb
              article={{
                section: { th: `ผลการค้นหา ${searchText}` }
              }}
            />
            {/* <ul className='breadcrumb'>
              <li>
                <a href='/'>หน้าแรก</a>
              </li>
              <li>{`ผลการค้นหา ${searchText}`}</li>
            </ul> */}

            <div className='row'>
              <div className='col-lg-8 col-md-12'>
                <h4 className='text-extra' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  ผลการค้นหา{' '}
                </h4>
                <h1 className='heading-title' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  "{searchText === null || searchText === 'null' ? ' ' : searchText}"
                </h1>
                <div className='row' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  {articles?.length < 1 ? (
                    <div className='col-12 no-article'>
                      <img src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                    </div>
                  ) : (
                    listArticles.map((item, index) => (
                      <div key={item.id} id={index + 1} className='col-6 col-md-4 mb-3'>
                        <CardV
                          data={item}
                          path={item.link}
                          description={false}
                          badge={true}
                          showPageviews={false}
                          //! DATALAYER
                          eventDataLayer={{
                            type: DATALAYER_TYPE.TRACK_POSITION,
                            router: router?.pathname,
                            section: 'ข่าวทั้งหมด',
                            position: 'ใต้:โฆษณาตำแหน่ง billboard',
                            data: {
                              block: '1',
                              title: item.title,
                              heading: `ผลการค้นหา ${searchText === null || searchText === 'null' ? ' ' : searchText}`,
                              index: index + 1
                            }
                          }}
                        />
                      </div>
                    ))
                  )}
                  {isFetching && <span className='fetch-data'> Fetching more list items... </span>}
                </div>
                <div id='postartice' className='ads-sticky-overflow'>
                  {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
                </div>
              </div>

              <div className='col-lg-4 col-md-12'>
                <StickyBox offsetTop={70} offsetBottom={70}>
                  {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.STICKY_1} data={ads} />}

                  <div className='widget-content' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                    <WidgetHeading heading='Latest News' />
                    <div className='block-content'>
                      {Array.isArray(lastNews) && lastNews.length < 1 ? (
                        <div className='no-article'>
                          <img className='--side' src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                        </div>
                      ) : (
                        <ul className='block-list'>
                          {lastNews.slice(0, 4).map((item, index) => (
                            <li key={index}>
                              <CardH
                                data={item}
                                large={false}
                                showPageviews={false}
                                //! DATALAYER
                                eventDataLayer={{
                                  type: DATALAYER_TYPE.TRACK_POSITION,
                                  router: router?.pathname,
                                  section: 'Latest News',
                                  position: 'ใต้:โฆษณาตำแหน่ง billboard',
                                  data: {
                                    block: '2',
                                    title: item.title,
                                    heading: 'Latest News',
                                    index: index + 1
                                  }
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.STICKY_2} data={ads} />}
                </StickyBox>
              </div>
            </div>
          </div>
        </SectionWrapper>
        <div id='postartice' className='ads-sticky-overflow'>
          {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
        </div>
        {!_.isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

export async function getServerSideProps({ req, res, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: [],
    lastNews: null,
    searchText: '',
    nextArticles: null,
    totalSearch: 0,
    ads: [],
    dataAdsHeader: null
  };
  const scriptBrowser = await GET('/api/headerAds');
  if (!_.isEmpty(scriptBrowser)) {
    initProps.dataAdsHeader = scriptBrowser || '';
  }
  initProps.searchText = query.q || null;
  // console.log('SEARCH', query.q, query.q.length);
  try {
    if (query?.q?.length > 70) {
      res.setHeader('location', '/category/latest-news');
      res.statusCode = 301;
      res.end();
    } else {
      const resData = await GET_ARTICLES(
        `/api/search${convertObjPath({
          q: query.q
        })}`,
        false,
        true
      );
      if (resData?.statusCode === 404 || _.isUndefined(resData)) {
        res.setHeader('location', '/category/latest-news');
        res.statusCode = 301;
        res.end();
      }
      if (!_.isEmpty(resData?.data)) {
        initProps.articles = resData?.data || [];
        initProps.lastNews = resData?.last_news || [];
        initProps.nextArticles = resData?.links || {};
        initProps.ads = resData?.ads || [];
      }
    }
  } catch (err) {
    console.log('==========> SEARCH ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }
  return {
    props: initProps
  };
}

export default PageSearch;
const SectionWrapper = styled.div`
  .fetch-data {
    display: block;
    text-align: center;
    font-size: 18pt;
  }
`;
